import { readonly } from 'vue'
import { IUsers } from '~/gateway/users/users.types'
import type { IUser } from './user.types'
import { apiClient } from '~/gateway/client'

let refreshTokenInterval: NodeJS.Timeout | null = null

export const useUser = () => {
  const userRole = useState<IUsers.Role>('userRole', () => 'user' as IUsers.Role)
  const state = useState<IUsers.Model<IUsers.Role> | null>('user', () => null)

  const setUser = <R extends IUsers.Role>(u: IUsers.Model<R> | null) => {
    state.value = u
    if (u) {
      userRole.value = u.role as IUsers.Role
    }
  }

  const signIn = async (params: IUser.SignIn.Params) => {
    try {
      const user = await apiClient<IUser.SignIn.ReturnType>('/auth/sign-in', {
        method: 'POST',
        body: params,
      })

      setUser(user)
      refreshAccessToken()
    } catch (error) {
      console.log('useUser.login', error)
    }
  }

  const signOut = async () => {
    try {
      await apiClient('/auth/sign-out', {
        method: 'POST',
      })
    } catch (error) {
      console.log('useUser.login', error)
    }

    if (refreshTokenInterval) {
      clearInterval(refreshTokenInterval)
      refreshTokenInterval = null
    }
    setUser(null)
    userRole.value = 'user'
    return navigateTo('/login')
  }

  const changePassword = async (params: IUser.ChangePassword.Params) => {
    try {
      return apiClient<IUser.ChangePassword.ReturnType>('/auth/change-password', {
        method: 'POST',
        body: params,
      })
    } catch (error) {
      console.error('useUser.changePassword', error)
    }
  }

  const getMe = async () => {
    try {
      const user = await apiClient<IUser.GetMe.ReturnType>('/auth/me')
      setUser(user)
      return user
    } catch (error) {
      console.log('useUser.getMe', error)
    }
  }

  const getUserByRegisterToken = async (token: string) => {
    try {
      return apiClient<IUser.GetMe.ReturnType>(`/auth/register/${token}`)
    } catch (error) {
      console.error('useUser.getUserByRegisterToken', error)
      return null
    }
  }

  const setupPassword = async (params: IUser.SetupPassword.Params) => {
    try {
      return apiClient<IUser.SetupPassword.ReturnType>('/auth/register/set-password', {
        method: 'POST',
        body: params,
      })
    } catch (error) {
      console.error('useUser.setupPassword', error)
    }
  }

  const getTokens = async () => {
    try {
      const config = useRuntimeConfig()

      await $fetch<IUser.GetTokens.ReturnType>('/auth/token/refresh', {
        method: 'POST',
        baseURL: config.public.apiUrl,
        credentials: 'include',
      })

      refreshAccessToken()
    } catch (error) {
      console.log('useUser.getTokens', error)
      throw error
    }
  }

  const refreshAccessToken = () => {
    if (refreshTokenInterval) {
      clearInterval(refreshTokenInterval)
    }

    const interval = 1_200_000 // ~ 20 min

    refreshTokenInterval = setInterval(async () => {
      try {
        await getTokens()
      } catch (error) {
        if (refreshTokenInterval) {
          clearInterval(refreshTokenInterval)
          refreshTokenInterval = null
        }
        return navigateTo('/login')
      }
    }, interval)
  }

  const actions = {
    getMe,
    signIn,
    signOut,
    getTokens,
    changePassword,
    refreshAccessToken,
    getUserByRegisterToken,
    setupPassword,
  }

  const getters = reactive({
    role: computed(() => userRole.value),

    isOwner: computed(() => state.value?.role === IUsers.Enum.Role.Owner),

    isAdmin: computed(
      () =>
        state.value?.role === IUsers.Enum.Role.Owner ||
        state.value?.role === IUsers.Enum.Role.Admin,
    ),

    isLoggedIn: computed(() => !!state.value?.email),

    partner: computed(() => state.value?.partner),
  })

  return {
    actions,
    getters,
    state: readonly(state) as Ref<IUsers.Model<IUsers.Role>>,
  }
}
